// Static config lifted from data.js (non-mock parts): timeframes, indicator defs,
// size buckets, exit presets. These stay client-side; values that the server cares
// about (sizing, targetPreset) are aligned to commands/schemas.ts where they cross
// the wire (see mappers.ts / P5).

// Legend chart TF token → server bar/indicator timeframe. All nine are screener-stored bars with server
// indicators (3Min and 30Min since the screener rework, #733).
export const TF_TO_SERVER: Record<string, string> = {
  '1': '1Min', '3': '3Min', '5': '5Min', '15': '15Min', '30': '30Min', '1H': '1H', '4H': '4H', 'D': '1D', 'W': '1W',
};
// Minutes per bar (the live candle's bucket: lib/bins.ts — D = midnight ET, W = Monday, 4H from 04:00 ET).
export const TF_MIN: Record<string, number> = {
  '1': 1, '3': 3, '5': 5, '15': 15, '30': 30, '1H': 60, '4H': 240, 'D': 1440, 'W': 10080,
};
// Order + labels of the timeframe tabs in the chart header.
export const TF_TABS = ['1', '3', '5', '15', '30', '1H', '4H', 'D', 'W'];
export const TF_LABEL: Record<string, string> = {
  '1': '1m', '3': '3m', '5': '5m', '15': '15m', '30': '30m', '1H': '1H', '4H': '4H', 'D': 'D', 'W': 'W',
};

// indicator config per timeframe (defaults). Keys match the server indicators API
// (values.{ema9,ema21,ema50,ema200,vwap,...}). All EMAs + VWAP default ON for every
// timeframe — the chart draws each line only when its data is available and hides it
// gracefully otherwise (e.g. EMA200 with < 200 bars). Toggles can still turn them off.
export function defaultIndicators() {
  // Buy Score OFF by default since the Volume Pulse (2026-10-13): the prototype's one sub-pane is the pulse.
  const all = { vwap: true, emas: true, mas: false, buyscore: false, volume: true, flags: false };
  return {
    '1': { ...all }, '3': { ...all }, '5': { ...all }, '15': { ...all }, '30': { ...all },
    '1H': { ...all }, '4H': { ...all }, 'D': { ...all }, 'W': { ...all },
  };
}

// EMA periods for the client-computed live tail / 3m fallback.
export const EMA_PERIODS: Record<string, number> = { ema9: 9, ema21: 21, ema50: 50, ema200: 200 };
// Line width (px) per EMA — thicker = slower/more structural. lightweight-charts
// LineWidth is 1..4, so these map 1:1.
export const EMA_WIDTH: Record<string, 1 | 2 | 3 | 4> = { ema9: 1, ema21: 2, ema50: 3, ema200: 4 };
// Kind → line color: EMAs blue, plain MAs (SMA) amber. Width says WHICH average,
// color says WHICH KIND.
export const MA_KIND_COLOR: Record<string, string> = { ema: '#4f8cff', ma: '#e8a13d' };

export const INDICATOR_DEFS = [
  { key: 'vwap', name: 'VWAP', sub: 'session', color: '#e0b15a', pane: 'price' },
  // Two INDEPENDENT sets, each all four periods (9/21/50/200), overlayable:
  // period is encoded by LINE WIDTH (EMA_WIDTH 1/2/3/4px), kind by COLOR.
  { key: 'emas', name: 'EMAs', sub: '9/21/50/200', color: '#4f8cff', pane: 'price' },
  { key: 'mas', name: 'MAs', sub: '9/21/50/200 · simple', color: '#e8a13d', pane: 'price' },
  // Buy score (1-100, 50 = balanced) — who paid up over the bar, scored SERVICE-side
  // from summed aggressor flow so the gauge and this pane are the same definition.
  { key: 'buyscore', name: 'Buy Score', sub: '1–100', color: '#38c08a', pane: 'sub' },
  // Volume Pulse: each bar ÷ the median of the 10 bars before it (lib/vol-pulse.ts).
  { key: 'volume', name: 'Rel Volume', sub: '× 10-bar median', color: '#3a4250', pane: 'sub' },
  // Model (AI) fire markers on the price pane: ● circles for the strong,
  // tape-informed MV_HOLD fires (p≥0.90). The ladder FSM flags (BC / TURN /
  // SR / FLIP / FF / FEED / RTR / S / P) are gone from the chart by request —
  // the ladder card and watchlist rows keep the FSM story.
  // OFF by default (operator, 2026-09-29: the SWH·1D / SWL·1W circles clutter
  // the chart) — this toggle turns them back on.
  { key: 'flags', name: 'AI fires', sub: 'model', color: '#38c08a', pane: 'price' },
];

// Size buckets. `full` (×1.0) = the worker's base position size; the others scale it.
// (small/2x are legend-only for now — worker support to be added.)
export const BUCKETS = [
  { key: 'lotto', name: 'Lotto', mult: 0.1 },
  { key: 'small', name: 'Small', mult: 0.25 },
  { key: 'half', name: 'Half', mult: 0.5 },
  { key: 'full', name: 'Full', mult: 1.0 },
  { key: '2x', name: '2x', mult: 2.0 },
];

// Sizing params (from /api/symbols/:symbol; falls back to the worker defaults).
export interface SizeParams { accountSize: number; riskPerTradePercent: number; maxPositionValuePercent: number; minPositionSize: number; }
export const DEFAULT_SIZE_PARAMS: SizeParams = {
  accountSize: 40000, riskPerTradePercent: 1.0, maxPositionValuePercent: 10.0, minPositionSize: 1,
};

// Base ("full") position size — mirrors the worker's calculatePositionSize:
//   risk-based  = floor(accountSize·risk% / (price·2% assumed stop))
//   value-based = floor(accountSize·maxPos% / price)
//   base        = min(risk-based, value-based)   (most conservative)
export function baseShares(price: number, p: SizeParams = DEFAULT_SIZE_PARAMS): number {
  if (!price || price <= 0) return 0;
  const riskAmount = price * 0.02;
  const riskBased = Math.floor((p.accountSize * (p.riskPerTradePercent / 100)) / riskAmount);
  const valueBased = Math.floor((p.accountSize * (p.maxPositionValuePercent / 100)) / price);
  return Math.max(0, Math.min(riskBased, valueBased));
}

// Bucket key → worker `sizing` value. '2x' is the worker's `double`; the rest match
// (worker support for `small` to be added).
export const SIZING_TO_SERVER: Record<string, string> = {
  lotto: 'lotto', small: 'small', half: 'half', full: 'full', '2x': 'double',
};
export const sizingFor = (key: string) => SIZING_TO_SERVER[key] || key;

// Shares for a bucket at a given price, using the live sizing params.
export function bucketShares(key: string, price = 0, p: SizeParams = DEFAULT_SIZE_PARAMS): number {
  const b = BUCKETS.find((x) => x.key === key);
  const shares = Math.floor(baseShares(price, p) * (b ? b.mult : 1));
  return Math.max(shares, p.minPositionSize || 1);
}

// exit-strategy presets. Server presets are UPPERCASE (TRIMMER/SCALPER/BALANCED/DEFAULT/NONE).
// `none` posts no take-profits — the position is held on the stop only (empty splits/rs).
export const TARGET_PRESETS = [
  { key: 'trimmer', label: 'Trimmer', splits: [0.9, 0.1], rs: [1.5, 3] },
  { key: 'scalper', label: 'Scalper', splits: [0.8, 0.2], rs: [1, 2] },
  { key: 'balanced', label: 'Balanced', splits: [0.55, 0.25, 0.2], rs: [1, 2, 3.5] },
  { key: 'default', label: 'Default', splits: [0.4, 0.3, 0.2, 0.1], rs: [1, 2, 3, 4.5] },
  { key: 'none', label: 'None', splits: [], rs: [] },
];
export const splitLabel = (splits: number[]) => splits.map((s) => Math.round(s * 100)).join(' / ');

// Trailing-stop sources for the position-card selector. 'levels' = the structural
// ratchet (V4 level map); the rest trail that 1m indicator line on each completed
// bar, sitting ¼×ATR (0.3% floor) below it. Keys are the worker's IndicatorEngine
// getIndicators() keys — the same keys the chart draws (INDICATOR_DEFS above).
export const TRAIL_SOURCES = [
  { key: 'levels', label: 'Struct', title: 'Ratchet under proven structure (level map)' },
  { key: 'ema9', label: 'E9', title: 'Trail the 1m EMA 9' },
  { key: 'ema21', label: 'E21', title: 'Trail the 1m EMA 21' },
  { key: 'ema50', label: 'E50', title: 'Trail the 1m EMA 50' },
  { key: 'ema200', label: 'E200', title: 'Trail the 1m EMA 200' },
  { key: 'vwap', label: 'VWAP', title: 'Trail session VWAP (extended 04:00 anchor)' },
];
